/**
 * Shared pipeline setting constants used by both TestGraphSettings
 * and the SNN node PropertiesPanel in the process engine.
 */

export const LLM_MODEL_OPTIONS = [
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast)' },
  { value: 'o4-mini', label: 'o4-mini (Fast Reasoning)' },
  { value: 'gpt-5.2-mini', label: 'GPT-5.2 Mini' },
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'o3-mini', label: 'o3-mini (Reasoning)' },
  { value: 'gpt-5.2', label: 'GPT-5.2' },
  { value: 'o3', label: 'o3 (Advanced Reasoning)' },
];

export const STAGE_MODEL_KEYS = [
  { key: 'classifier', label: 'Query Classifier', hint: 'Routes query type (fast model ideal)' },
  { key: 'conversational', label: 'Conversational', hint: 'Conversational replies (quality matters)' },
  { key: 'decomposition', label: 'Decomposition & Expansion', hint: 'Query splitting/expansion (fast model ideal)' },
  { key: 'aqh', label: 'Abstract Query Handler', hint: 'Complex aggregate queries (medium quality)' },
  { key: 'distillation', label: 'Context Distillation', hint: 'Evidence noise reduction (fast model ideal)' },
  { key: 'answer_render', label: 'Answer Rendering', hint: 'Core answer generation (highest quality)' },
  { key: 'judge', label: 'Judge & Refine', hint: 'Quality assurance pass (strong reasoning)' },
];

export const MODEL_PRESETS = {
  fast: {
    label: 'Fast',
    description: 'gpt-4o-mini for all stages',
    models: { classifier: 'gpt-4o-mini', conversational: 'gpt-4o-mini', decomposition: 'gpt-4o-mini', aqh: 'gpt-4o-mini', distillation: 'gpt-4o-mini', answer_render: 'gpt-4o-mini', judge: 'gpt-4o-mini' },
  },
  balanced: {
    label: 'Balanced',
    description: 'Fast models for prep, quality for answers',
    models: { classifier: 'gpt-4o-mini', conversational: 'gpt-4o', decomposition: 'gpt-4o-mini', aqh: 'gpt-4o', distillation: 'gpt-4o-mini', answer_render: 'gpt-4o', judge: 'gpt-4o' },
  },
  quality: {
    label: 'Quality',
    description: 'gpt-4o for all stages',
    models: { classifier: 'gpt-4o', conversational: 'gpt-4o', decomposition: 'gpt-4o', aqh: 'gpt-4o', distillation: 'gpt-4o', answer_render: 'gpt-4o', judge: 'gpt-4o' },
  },
  custom: {
    label: 'Custom',
    description: 'Choose per-stage models',
    models: null,
  },
};

export const MLCF_CONTEXT_STRICTNESS_OPTIONS = [
  { value: 'strict', label: 'Strict' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'permissive', label: 'Permissive' },
];

export const CONTEXT_POTENCY_MODE_OPTIONS = [
  { value: 'auto', label: 'Auto (dynamic)' },
  { value: 'manual', label: 'Manual' },
];

export const PIPELINE_DEFAULTS = {
  // Simulation
  simulate_duration_ms: 200.0,
  simulate_step_ms: 20.0,
  max_recursive_ticks: 10,
  bias_hz: 50.0,
  gain: 20.0,
  min_background_rate_hz: 0.0,
  // Rate coding
  rate_coding_min_hz: 8.0,
  rate_coding_max_hz: 220.0,
  rate_coding_base_hz: 50.0,
  softmax_temperature: 0.35,
  // MLCF
  mlcf_enabled: true,
  mlcf_identity_filter: true,
  mlcf_scope_filter: true,
  mlcf_constraint_filter: true,
  mlcf_context_filter: true,
  mlcf_structural_filter: true,
  mlcf_identity_threshold: 0.5,
  mlcf_scope_overlap_threshold: 0.3,
  mlcf_context_strictness: 'moderate',
  mlcf_min_survivors: 1,
  // CRS
  crs_enabled: true,
  crs_semantic_weight: 0.25,
  crs_node_type_compat_weight: 0.15,
  crs_intent_affinity_weight: 0.15,
  crs_scope_intersection_weight: 0.15,
  crs_causal_modality_weight: 0.10,
  crs_authority_weight: 0.08,
  crs_gate_readiness_weight: 0.07,
  crs_exception_alignment_weight: 0.05,
  crs_evidence_threshold: 0.3,
  crs_default_unscored_relevance: 0.3,
  // Query handling
  enable_abstract_query_handler: true,
  max_gap_fill_iterations: 2,
  graph_traversal_depth: 2,
  expand_with_vocabulary: false,
  // Context bundle
  context_potency_mode: 'auto',
  context_potency_threshold: 0.26,
  auto_potency_boost_multiplier: 1.3,
  include_verbatim_nodes: true,
  enable_judge: true,
  enable_context_distillation: true,
  enable_cross_encoder_rerank: false,
  // Propagation
  propagation_evidence_threshold: 0.15,
  // Semantic Receptors
  enable_ser: true,
  ser_temperature: 0.35,
  ser_bm25_boost: 0.1,
};

export const PIPELINE_TUNING_PRESETS = {
  precision: {
    label: 'Precision',
    description: 'Narrow factual / medical / legal queries',
    settings: {
      min_similarity: 0.55,
      similarity_top_k: 100,
      softmax_temperature: 0.20,
      propagation_evidence_threshold: 0.20,
      context_potency_mode: 'manual',
      context_potency_threshold: 0.30,
      enable_judge: true,
      enable_context_distillation: true,
      compact_render_mode: true,
      capture_synapse_fire_stats: true,
      mlcf_context_strictness: 'strict',
      mlcf_identity_threshold: 0.6,
      enable_ser: true,
      ser_temperature: 0.25,
      max_recursive_ticks: 8,
    },
  },
  recall: {
    label: 'Recall',
    description: 'Exploratory / broad knowledge queries',
    settings: {
      min_similarity: 0.40,
      similarity_top_k: 250,
      softmax_temperature: 0.50,
      propagation_evidence_threshold: 0.08,
      context_potency_mode: 'auto',
      auto_potency_boost_multiplier: 1.2,
      enable_judge: true,
      expand_with_vocabulary: true,
      decompose_query: true,
      mlcf_context_strictness: 'permissive',
      max_recursive_ticks: 15,
      simulate_duration_ms: 400,
    },
  },
  fge_optimized: {
    label: 'FGE v5 Optimized',
    description: 'Tuned for FGE v5 hierarchy graphs',
    settings: {
      enable_ser: true,
      ser_temperature: 0.25,
      compact_render_mode: true,
      capture_synapse_fire_stats: true,
      softmax_temperature: 0.25,
      propagation_evidence_threshold: 0.10,
      max_recursive_ticks: 15,
      simulate_duration_ms: 350,
      crs_scope_intersection_weight: 0.20,
      crs_semantic_weight: 0.25,
      context_potency_mode: 'auto',
      auto_potency_boost_multiplier: 1.5,
      enable_context_distillation: false,
    },
  },
};

export const PIPELINE_RANGES = {
  // Simulation
  simulate_duration_ms:   { min: 50, max: 2000, step: 10 },
  simulate_step_ms:       { min: 5,  max: 200,  step: 5 },
  max_recursive_ticks:    { min: 1,  max: 50,   step: 1 },
  bias_hz:                { min: 0,  max: 200,  step: 5 },
  gain:                   { min: 1,  max: 100,  step: 1 },
  min_background_rate_hz: { min: -10, max: 10,  step: 0.5 },
  // Rate coding
  rate_coding_min_hz:     { min: 0,    max: 100, step: 1 },
  rate_coding_max_hz:     { min: 50,   max: 500, step: 10 },
  rate_coding_base_hz:    { min: 5,    max: 200, step: 5 },
  softmax_temperature:    { min: 0.05, max: 2.0, step: 0.05 },
  // MLCF thresholds
  mlcf_identity_threshold:      { min: 0, max: 1, step: 0.05 },
  mlcf_scope_overlap_threshold: { min: 0, max: 1, step: 0.05 },
  mlcf_min_survivors:           { min: 0, max: 10, step: 1 },
  // CRS weights
  crs_semantic_weight:             { min: 0, max: 0.5, step: 0.01 },
  crs_node_type_compat_weight:     { min: 0, max: 0.5, step: 0.01 },
  crs_intent_affinity_weight:      { min: 0, max: 0.5, step: 0.01 },
  crs_scope_intersection_weight:   { min: 0, max: 0.5, step: 0.01 },
  crs_causal_modality_weight:      { min: 0, max: 0.5, step: 0.01 },
  crs_authority_weight:            { min: 0, max: 0.5, step: 0.01 },
  crs_gate_readiness_weight:       { min: 0, max: 0.5, step: 0.01 },
  crs_exception_alignment_weight:  { min: 0, max: 0.5, step: 0.01 },
  crs_evidence_threshold:          { min: 0.05, max: 0.8, step: 0.05 },
  crs_default_unscored_relevance:  { min: 0.05, max: 0.8, step: 0.05 },
  // Context bundle
  context_potency_threshold:       { min: 0.01, max: 0.80, step: 0.01 },
  auto_potency_boost_multiplier:   { min: 0.5,  max: 3.0,  step: 0.1 },
  // Propagation
  propagation_evidence_threshold:  { min: 0.01, max: 0.5, step: 0.01 },
  // SER
  ser_temperature:                 { min: 0.05, max: 2.0, step: 0.05 },
  ser_bm25_boost:                  { min: 0,    max: 1.0, step: 0.05 },
};
